// LeadsDirectory.jsx — completo com Agendar • Converter • Editar
import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useQuery, useQueryClient } from "react-query";
import {
  Badge,
  Button,
  Label,
  Modal,
  Pagination,
  Select,
  Table,
  Textarea,
  TextInput,
} from "flowbite-react";
import { IoMdSearch } from "react-icons/io";
import {
  HiOutlineAdjustmentsHorizontal,
  HiFunnel,
  HiOutlineCloudArrowUp,
  HiOutlineTrash,
} from "react-icons/hi2";
import {
  HiFilter,
  HiOutlineDocumentText,
  HiPlus,
  HiInformationCircle,
  HiClock,
} from "react-icons/hi";
import { FaRegEdit } from "react-icons/fa";
import { PiChatsTeardropLight } from "react-icons/pi";
import { MdAutoFixHigh } from "react-icons/md";
import axios from "../../config/axiosInstance";
import { LEADS, CRM, TEAMS, USERS, BUDGETS } from "../../config/apiConfig"; // 👈 confira seus endpoints
import LeadCreateForm from "../forms/LeadCreateForm";
import { useApplication } from "../../contexts/ApplicationContext";
import { HiDownload } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { FiSettings } from "react-icons/fi";

const LEAD_STATUS = [
  "new",
  "working",
  "qualified",
  "unqualified",
  "won",
  "lost",
];
const LEAD_SOURCE = [
  "manual",
  "website",
  "whatsapp",
  "instagram",
  "facebook",
  "google_ads",
  "organic",
  "referral",
  "event",
  "import",
  "lead-generator",
];
const LEAD_TYPE = ["lead", "client"];

const LEAD_TYPE_META = {
  lead: { label: "Lead", color: "info" },
  client: { label: "Cliente", color: "success" },
};

// ======= Metadados visuais =======
const STATUS_META = {
  new: { label: "Novo", color: "info" },
  working: { label: "Em andamento", color: "indigo" },
  qualified: { label: "Qualificado", color: "success" },
  unqualified: { label: "Descartado", color: "gray" },
  won: { label: "Ganho", color: "green" },
  lost: { label: "Perdido", color: "failure" },
};
const SOURCE_META = {
  manual: { label: "Manual", color: "gray" },
  website: { label: "Site", color: "info" },
  whatsapp: { label: "WhatsApp", color: "green" },
  instagram: { label: "Instagram", color: "pink" },
  facebook: { label: "Facebook", color: "blue" },
  google_ads: { label: "Google Ads", color: "warning" },
  organic: { label: "Orgânico", color: "success" },
  referral: { label: "Indicação", color: "purple" },
  event: { label: "Evento", color: "cyan" },
  import: { label: "Importado", color: "gray" },
  "lead-generator": { label: "Gerador de contatos", color: "indigo" },
};

const BUDGET_STATUS_COLORS = {
  draft: "gray",
  sent: "info",
  approved: "success",
  approved_sent: "success",
  cancelled: "failure",
  canceled: "failure",
  expired: "failure",
};

const formatCurrency = (value = 0) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number(value) || 0
  );

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status || "—", color: "gray" };
  return (
    <Badge color={meta.color} className="w-fit mx-auto text-[10px] px-1 py-0">
      {meta.label}
    </Badge>
  );
};
const SourceBadge = ({ source }) => {
  const meta = SOURCE_META[source] || { label: source || "—", color: "gray" };
  return (
    <Badge color={meta.color} className="w-fit mx-auto">
      {meta.label}
    </Badge>
  );
};

const TypeBadge = ({ type }) => {
  const meta = LEAD_TYPE_META[type] || { label: type || "—", color: "gray" };
  return (
    <Badge color={meta.color} className="w-fit mx-auto text-[11px]">
      {meta.label}
    </Badge>
  );
};

const StageInfoCell = ({ stage, cards = [] }) => {
  const fallbackStageRef = cards?.find((card) => card?.stage)?.stage ?? null;
  const resolvedStage = stage || fallbackStageRef;
  const stageName = resolvedStage?.name;
  const funnel = resolvedStage?.funnel;
  const funnelName = funnel?.name;
  const funnelIds = new Set(
    cards?.map((card) => card?.stage?.funnel?.id).filter((id) => Boolean(id))
  );
  if (funnel?.id) funnelIds.add(funnel.id);
  const extraFunnels =
    funnelIds.size - (funnel?.id ? 1 : 0) > 0
      ? funnelIds.size - (funnel?.id ? 1 : 0)
      : 0;
  if (!stageName && !funnelName) {
    return (
      <div className="flex items-center gap-1 text-xs text-gray-400">
        <HiFilter className="text-sm text-gray-400" />
        Nenhum funil
      </div>
    );
  }
  return (
    <div className="flex flex-col items-start gap-0">
      <div className="flex items-center gap-1 text-xs font-semibold text-gray-900">
        <HiFilter className="text-base text-indigo-500" />
        <span>{stageName || "—"}</span>
        {extraFunnels > 0 && (
          <span className="inline-flex items-center gap-0.5 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">
            <span>+{extraFunnels}</span>
          </span>
        )}
      </div>
      <div className="text-[10px] text-gray-500">{funnelName || "—"}</div>
    </div>
  );
};

const formatStageSummary = (stage) => {
  const parts = [];

  if (stage?.name) parts.push(stage.name);
  if (stage?.funnel?.name) parts.push(stage.funnel.name);
  return parts.length ? parts.join(" • ") : null;
};

function nextActionColor(nextActionAt) {
  if (!nextActionAt) return "text-gray-400";
  const now = moment().startOf("day");
  const due = moment(nextActionAt).startOf("day");
  if (due.isBefore(now)) return "text-rose-600";
  if (due.isSame(now)) return "text-amber-600";
  return "text-emerald-700";
}
const NextActionPill = ({ type, date }) => {
  const c = nextActionColor(date);
  return (
    <span
      className={`justify-center flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full border ${c} border-current w-fit`}
    >
      <MdAutoFixHigh className="text-[13px]" />
      {type ? type : "—"} <span className="opacity-80">•</span>{" "}
      {date ? moment(date).format("DD/MM") : "—"}
    </span>
  );
};

const HISTORY_EVENT_LABELS = {
  lead_created: "Lead criado",
  lead_updated: "Dados atualizados",
  stage_changed: "Etapa alterada",
  status_changed: "Status atualizado",
  owner_changed: "Responsável alterado",
  note_added: "Nota registrada",
  lead_converted: "Lead convertido",
  lead_lost: "Lead perdido",
  tag_changed: "Tags atualizadas",
  contact_added: "Contato associado",
  contact_updated: "Contato atualizado",
  contact_deleted: "Contato excluído",
};

const describeHistoryMetadata = (metadata) => {
  if (!metadata) return null;
  if (typeof metadata === "string") return metadata;
  if (metadata.note) return metadata.note;
  if (metadata.details) return metadata.details;
  if (metadata.description) return metadata.description;
  if (metadata?.message) return metadata.message;
  if (Array.isArray(metadata?.changes)) {
    return "Contato atualizado";
  }

  const values = Object.entries(metadata || {})
    .filter(
      ([key, value]) =>
        key !== "contactId" &&
        value != null &&
        (typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean" ||
          Array.isArray(value))
    )
    .map(([, value]) => {
      if (Array.isArray(value)) {
        return value.join(", ");
      }
      return String(value);
    });
  return values.length ? values.join(" • ") : null;
};

const onlyDigits = (input = "") => String(input || "").replace(/\D/g, "");

const formatPhoneNumber = (value = "") => {
  const digits = onlyDigits(value);
  if (!digits) return null;
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  }
  return digits
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2");
};

export default function LeadsDirectory() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, userPermission } = useApplication();
  const isUserOnly = userPermission === "USER";
  const userTeamIds = useMemo(
    () => (user?.Teams || []).map((team) => team?.id).filter(Boolean),
    [user]
  );

  // ===== Server params =====
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [createOpen, setCreateOpen] = useState(false);

  // ===== busca + filtros =====
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [leadType, setLeadType] = useState("all");
  const [ownerId, setOwnerId] = useState("all");
  const [tagId, setTagId] = useState("all");
  const [appliedFilters, setAppliedFilters] = useState({
    q: "",
    status: "all",
    source: "all",
    leadType: "all",
    ownerId: "all",
    tagId: "all",
  });
  const applyFilters = (nextFilters) => {
    const filtersToApply = nextFilters ?? {
      q: q.trim(),
      status,
      source,
      leadType,
      ownerId,
      tagId,
    };
    setAppliedFilters(filtersToApply);
    setPage(1);
    setFiltersOpen(false);
  };
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [newActionsOpen, setNewActionsOpen] = useState(false);
  const [newMenuSlideIn, setNewMenuSlideIn] = useState(false);
  const [mobileLeadMenuOpen, setMobileLeadMenuOpen] = useState(false);
  const [activeMobileLead, setActiveMobileLead] = useState(null);
  const [mobileMenuSlideIn, setMobileMenuSlideIn] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [deletingLeadId, setDeletingLeadId] = useState(null);
  const [statusEditingLeadId, setStatusEditingLeadId] = useState(null);
  const [statusEditingValue, setStatusEditingValue] = useState("");
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [sourceEditingLeadId, setSourceEditingLeadId] = useState(null);
  const [sourceEditingValue, setSourceEditingValue] = useState("");
  const [sourceUpdatingId, setSourceUpdatingId] = useState(null);
  const [typeEditingLeadId, setTypeEditingLeadId] = useState(null);
  const [typeEditingValue, setTypeEditingValue] = useState("");
  const [typeUpdatingId, setTypeUpdatingId] = useState(null);

  const IMPORT_TEMPLATE = [
    { label: "Razão Social", key: "razão_social" },
    { label: "Nome", key: "nome" },
    { label: "Documento", key: "documento" },
    { label: "Telefone", key: "telefone" },
    { label: "E-mail", key: "email" },
    { label: "Endereço", key: "endereço" },
    { label: "Bairro", key: "bairro" },
    { label: "Cidade", key: "cidade" },
    { label: "Estado", key: "estado" },
    { label: "País", key: "pais" },
    { label: "Observações", key: "observações" },
  ];
  const IMPORT_SAMPLE = [
    "ACME Ltda",
    "João Silva",
    "12345678000199",
    "(11) 99999-0000",
    "joao@acme.com",
    "Rua Exemplo",
    "Centro",
    "São Paulo",
    "SP",
    "Brasil",
    "Lead da feira",
  ];

  const downloadTemplate = () => {
    const header = IMPORT_TEMPLATE.map((col) => col.label).join(";");
    const example =
      "ACME Ltda;João Silva;12345678000199;(11) 99999-0000;joao@acme.com;Rua Exemplo;Centro;São Paulo;SP;Brasil;Lead da feira";
    const csv = `\uFEFFsep=;\n${header}\n${example}\n`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "modelo-leads-importacao.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportSubmit = async () => {
    if (!importFile) return;
    const fd = new FormData();
    fd.append("file", importFile);
    setImporting(true);
    try {
      await axios.post(LEADS.IMPORT, fd, {
        headers: { "Content-Type": undefined },
      });
      setImportOpen(false);
      setImportFile(null);
      refetchLeads();
      queryClient.invalidateQueries("admin-customers");
    } catch (err) {
      console.error("Erro ao importar leads:", err);
      window.alert("Não foi possível importar o arquivo.");
    } finally {
      setImporting(false);
    }
  };

  // ===== dados de apoio =====
  const [tags, setTags] = useState([]); // {id, name, color}
  const {
    data: ownersData = [],
    isLoading: ownersLoading,
    refetch: refetchOwners,
  } = useQuery(
    ["admin-customers-owners"],
    async () => {
      const { data: payload } = await axios.get(
        `${USERS.GET_USERS}?itemsPerPage=-1`
      );
      const list =
        payload?.data?.data ||
        payload?.data ||
        payload?.users ||
        payload?.rows ||
        payload ||
        [];
      return Array.isArray(list) ? list : [];
    },
    {
      staleTime: 1000 * 60,
      refetchOnWindowFocus: false,
    }
  );

  // ===== ações/modais =====
  const [leadForEdit, setLeadForEdit] = useState(null);
  const [leadForSchedule, setLeadForSchedule] = useState(null);
  const [leadForConvert, setLeadForConvert] = useState(null);
  const [leadDetailError, setLeadDetailError] = useState("");

  // schedule form
  const [schedType, setSchedType] = useState("followup");
  const [schedWhen, setSchedWhen] = useState(""); // datetime-local
  const [schedNotes, setSchedNotes] = useState("");
  const [savingSchedule, setSavingSchedule] = useState(false);

  // edit form
  const [editOpen, setEditOpen] = useState(false);
  const [detailActiveTab, setDetailActiveTab] = useState("details");
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [budgetsEntries, setBudgetsEntries] = useState([]);
  const [budgetsLoading, setBudgetsLoading] = useState(false);
  const [budgetsError, setBudgetsError] = useState("");
  const [funnelsEntries, setFunnelsEntries] = useState([]);
  const [expandedBudgetIds, setExpandedBudgetIds] = useState([]);
  const [detailSubmitting, setDetailSubmitting] = useState(false);

  // convert form
  const [convertOpen, setConvertOpen] = useState(false);
  const [funnels, setFunnels] = useState([]); // {id, name}
  const [stages, setStages] = useState([]); // {id, name, ...}
  const [selectedFunnelDetail, setSelectedFunnelDetail] = useState(null);
  const [funnelId, setFunnelId] = useState("");
  const [stageId, setStageId] = useState("");
  const [responsibleId, setResponsibleId] = useState("");
  const [responsibleOptions, setResponsibleOptions] = useState([]);
  const [cardTitle, setCardTitle] = useState("");
  const [savingConvert, setSavingConvert] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkFunnelId, setBulkFunnelId] = useState("");
  const [bulkStages, setBulkStages] = useState([]);
  const [bulkStageId, setBulkStageId] = useState("");
  const [bulkResponsibleOptions, setBulkResponsibleOptions] = useState([]);
  const [bulkResponsibleId, setBulkResponsibleId] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  // ===== debounce =====
  useEffect(() => {
    if (isUserOnly && user?.id) {
      setOwnerId(String(user.id));
    }
  }, [isUserOnly, user?.id]);

  useEffect(() => {
    if (!leadForConvert) return;
    setCardTitle(
      leadForConvert.name ||
        leadForConvert.companyName ||
        "Novo card para contato"
    );
    setResponsibleId(leadForConvert.ownerId || "");
  }, [leadForConvert]);

  const mapUsersToOptions = (users = []) =>
    (users || [])
      .map((u) => ({
        id: u.id,
        name: u.name || u.email || `Usuário ${u.id}`,
      }))
      .filter((u) => u.id);

  // ===== fetch tags (ajuste endpoints) =====
  useEffect(() => {
    (async () => {
      try {
        const tRes = await axios.get("/tags?itemsPerPage=-1");
        setTags(
          tRes?.data?.data?.data ||
            tRes?.data?.data ||
            tRes?.data ||
            tRes?.data?.tags ||
            []
        );
      } catch (error) {
        console.error("Erro ao carregar tags:", error);
      }
    })();
  }, []);

  useEffect(() => {
    if (!leadForEdit) {
      setFunnelsEntries([]);
      return;
    }
    setFunnelsEntries(buildLeadFunnelsFromLead(leadForEdit));
  }, [leadForEdit]);

  // ===== query leads =====
  const { data, isLoading } = useQuery(
    [
      "leads",
      page,
      pageSize,
      appliedFilters.q,
      appliedFilters.status,
      appliedFilters.source,
      appliedFilters.leadType,
      appliedFilters.ownerId,
      appliedFilters.tagId,
    ],
    async () => {
      const resolvedOwnerId = isUserOnly
        ? user?.id
          ? String(user.id)
          : undefined
        : appliedFilters.ownerId;
      const ownerParam =
        resolvedOwnerId && resolvedOwnerId !== "all"
          ? resolvedOwnerId
          : undefined;

      const params = {
        page,
        itemsPerPage: pageSize,
        search: appliedFilters.q || undefined,
        status:
          appliedFilters.status === "all" ? undefined : appliedFilters.status,
        source:
          appliedFilters.source === "all" ? undefined : appliedFilters.source,
        leadType:
          appliedFilters.leadType === "all"
            ? undefined
            : appliedFilters.leadType,
        ownerId: ownerParam,
        tagId:
          appliedFilters.tagId === "all" ? undefined : appliedFilters.tagId,
      };
      const res = await axios.get(LEADS.LIST, { params });
      return res?.data?.data || { data: [], totalPages: 1, totalCount: 0 };
    },
    { keepPreviousData: true }
  );
  const rows = data?.data || [];
  const totalPages = data?.totalPages || 1;
  const totalCount = data?.totalCount || 0;
  const leadById = useMemo(
    () => new Map(rows.map((lead) => [lead.id, lead])),
    [rows]
  );
  const selectedCount = selectedLeadIds.size;
  const isAllSelected =
    rows.length > 0 && rows.every((lead) => selectedLeadIds.has(lead.id));
  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedLeadIds(new Set());
      return;
    }
    const allIds = rows
      .map((lead) => lead.id)
      .filter((id) => typeof id !== "undefined" && id !== null);
    setSelectedLeadIds(new Set(allIds));
  };
  const clearSelection = () => setSelectedLeadIds(new Set());
  const toggleLeadSelection = (leadId) => {
    if (!leadId) return;
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const openCardModal = (lead) => {
    setLeadForConvert(lead);
    setCardTitle(lead?.name || lead?.companyName || "Novo card");
    setResponsibleId(lead?.ownerId || "");
    setConvertOpen(true);
  };

  // ===== chips resumo =====
  const activeChips = useMemo(() => {
    const chips = [];
    if (appliedFilters.q)
      chips.push({
        k: "q",
        label: `Busca: "${appliedFilters.q}"`,
        clear: () => {
          setQ("");
          const next = { ...appliedFilters, q: "" };
          setAppliedFilters(next);
          setPage(1);
        },
      });
    if (appliedFilters.status && appliedFilters.status !== "all")
      chips.push({
        k: "status",
        label: `Status: ${
          STATUS_META[appliedFilters.status]?.label || appliedFilters.status
        }`,
        clear: () => {
          setStatus("all");
          const next = { ...appliedFilters, status: "all" };
          setAppliedFilters(next);
          setPage(1);
        },
      });
    if (appliedFilters.source && appliedFilters.source !== "all")
      chips.push({
        k: "source",
        label: `Origem: ${
          SOURCE_META[appliedFilters.source]?.label || appliedFilters.source
        }`,
        clear: () => {
          setSource("all");
          const next = { ...appliedFilters, source: "all" };
          setAppliedFilters(next);
          setPage(1);
        },
      });
    if (appliedFilters.leadType && appliedFilters.leadType !== "all")
      chips.push({
        k: "leadType",
        label: `Tipo: ${
          LEAD_TYPE_META[appliedFilters.leadType]?.label ||
          appliedFilters.leadType
        }`,
        clear: () => {
          setLeadType("all");
          const next = { ...appliedFilters, leadType: "all" };
          setAppliedFilters(next);
          setPage(1);
        },
      });
    if (appliedFilters.ownerId && appliedFilters.ownerId !== "all") {
      const name =
        ownersData.find((o) => String(o.id) === String(appliedFilters.ownerId))
          ?.name || appliedFilters.ownerId;
      chips.push({
        k: "owner",
        label: `Dono: ${name}`,
        clear: () => {
          setOwnerId("all");
          const next = { ...appliedFilters, ownerId: "all" };
          setAppliedFilters(next);
          setPage(1);
        },
      });
    }
    if (appliedFilters.tagId && appliedFilters.tagId !== "all") {
      const name =
        tags.find((t) => String(t.id) === String(appliedFilters.tagId))?.name ||
        appliedFilters.tagId;
      chips.push({
        k: "tag",
        label: `Tag: ${name}`,
        clear: () => {
          setTagId("all");
          const next = { ...appliedFilters, tagId: "all" };
          setAppliedFilters(next);
          setPage(1);
        },
      });
    }
    return chips;
  }, [appliedFilters, ownersData, tags]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest("[data-inline-edit]")) return;
      if (statusEditingLeadId || sourceEditingLeadId || typeEditingLeadId) {
        setStatusEditingLeadId(null);
        setSourceEditingLeadId(null);
        setTypeEditingLeadId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [statusEditingLeadId, sourceEditingLeadId, typeEditingLeadId]);

  const clearAll = () => {
    const resetFilters = {
      q: "",
      status: "all",
      source: "all",
      leadType: "all",
      ownerId: "all",
      tagId: "all",
    };
    setQ("");
    setStatus("all");
    setSource("all");
    setLeadType("all");
    setOwnerId("all");
    setTagId("all");
    setAppliedFilters(resetFilters);
    setPage(1);
  };

  const openNewActions = () => setNewActionsOpen(true);
  const closeNewActions = () => setNewActionsOpen(false);
  const handleNewContact = () => {
    setCreateOpen(true);
    closeNewActions();
  };
  const handleImportContacts = () => {
    setImportOpen(true);
    closeNewActions();
  };

  const openMobileLeadMenu = (lead) => {
    setActiveMobileLead(lead);
    setMobileLeadMenuOpen(true);
  };

  const closeMobileLeadMenu = () => {
    setMobileLeadMenuOpen(false);
    setActiveMobileLead(null);
  };

  const handleMobileLeadEdit = () => {
    if (!activeMobileLead) return;
    onEdit(activeMobileLead);
    closeMobileLeadMenu();
  };

  const handleMobileLeadConvert = () => {
    if (!activeMobileLead) return;
    openCardModal(activeMobileLead);
    closeMobileLeadMenu();
  };

  const handleMobileLeadDelete = () => {
    if (!activeMobileLead) return;
    handleDeleteLead(activeMobileLead);
    closeMobileLeadMenu();
  };

  const refetchLeads = () =>
    queryClient.invalidateQueries({
      predicate: (q) => q.queryKey?.[0] === "leads",
    });

  const closeEditModal = useCallback(() => {
    setEditOpen(false);
    setLeadForEdit(null);
    setDetailActiveTab("details");
    setHistoryEntries([]);
    setHistoryError("");
    setHistoryLoading(false);
    setBudgetsEntries([]);
    setBudgetsError("");
    setBudgetsLoading(false);
    setExpandedBudgetIds([]);
  }, []);

  const editInitialData = useMemo(() => {
    if (!leadForEdit) return null;
    return {
      ...leadForEdit,
      tagId: leadForEdit.tagId || leadForEdit?.tag?.id || "",
    };
  }, [leadForEdit]);

  const handleUpdateLead = async (payload) => {
    if (!leadForEdit?.id) {
      throw new Error("Lead inválido para atualização");
    }
    const { data } = await axios.put(LEADS.UPDATE(leadForEdit.id), payload);
    return data?.data || data;
  };

  const handleEditSuccess = () => {
    closeEditModal();
    refetchLeads();
  };

  const loadLeadHistory = async (leadId) => {
    const targetLeadId = leadId || leadForEdit?.id;
    if (!targetLeadId) return;
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const { data } = await axios.get(LEADS.HISTORY(targetLeadId));
      const payload = data?.data || data?.rows || data || [];
      const entries = Array.isArray(payload) ? payload : [];
      const sorted = [...entries].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setHistoryEntries(sorted);
    } catch (error) {
      console.error("Erro ao buscar histórico do lead:", error);
      setHistoryError("Não foi possível carregar o histórico agora.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadLeadBudgets = async (leadId) => {
    const targetLeadId = leadId || leadForEdit?.id;
    if (!targetLeadId) return;
    setBudgetsLoading(true);
    setBudgetsError("");
    try {
      const { data } = await axios.get(BUDGETS.LIST, {
        params: { leadId: targetLeadId, limit: 20 },
      });
      const payload =
        data?.data?.budgets ?? data?.budgets ?? data?.data ?? data?.rows ?? [];
      const entries = Array.isArray(payload) ? payload : [];
      const sorted = [...entries].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setBudgetsEntries(sorted);
    } catch (error) {
      console.error("Erro ao buscar orçamentos do lead:", error);
      setBudgetsError("Não foi possível carregar os orçamentos agora.");
    } finally {
      setBudgetsLoading(false);
    }
  };

  const loadLeadDetail = useCallback(async (leadId) => {
    if (!leadId) return;
    setLeadDetailError("");
    try {
      const { data } = await axios.get(LEADS.GET(leadId));
      const payload = data?.data || data?.lead || data?.rows || data || null;
      setLeadForEdit(payload);
    } catch (error) {
      console.error("Erro ao carregar lead:", error);
      setLeadDetailError("Não foi possível carregar o lead selecionado.");
      setLeadForEdit(null);
    }
  }, []);

  const buildLeadFunnelsFromLead = (lead) => {
    const cards = Array.isArray(lead?.cards) ? lead.cards : [];
    const map = new Map();
    cards.forEach((card) => {
      const stage = card.stage || card.Stage || {};
      const funnel =
        stage.funnel ||
        stage.Funnel ||
        card.funnel ||
        card.Funnel ||
        stage.funnelData ||
        null;
      const funnelId =
        String(
          funnel?.id ||
            funnel?.funnelId ||
            stage?.funnelId ||
            card?.stage?.funnelId ||
            ""
        ) || null;
      if (!funnelId) return;
      const stageName =
        stage?.name ||
        card.stageName ||
        card.stage?.name ||
        "Etapa não informada";
      const funnelName =
        funnel?.name ||
        stage?.funnelName ||
        card.funnelName ||
        "Funil sem nome";
      const updatedAt =
        card.updatedAt ||
        card.updatedOn ||
        card.createdAt ||
        stage?.updatedAt ||
        stage?.createdAt ||
        null;
      const entry = {
        funnelId,
        funnelName,
        stageName,
        stageId: stage?.id || card.stageId || null,
        cardId: card.id,
        updatedAt: updatedAt ? new Date(updatedAt).toISOString() : null,
      };
      const previous = map.get(funnelId);
      if (!previous) {
        map.set(funnelId, entry);
        return;
      }
      const prevTime = previous.updatedAt
        ? new Date(previous.updatedAt).getTime()
        : 0;
      const currTime = entry.updatedAt
        ? new Date(entry.updatedAt).getTime()
        : 0;
      if (currTime >= prevTime) {
        map.set(funnelId, entry);
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return bTime - aTime;
    });
  };

  const toggleBudgetItems = (budgetId) => {
    setExpandedBudgetIds((prev) =>
      prev.includes(budgetId)
        ? prev.filter((id) => id !== budgetId)
        : [...prev, budgetId]
    );
  };

  const openDetailModal = (lead) => {
    if (!lead?.id) return;
    setLeadForEdit(null);
    setDetailActiveTab("details");
    setHistoryEntries([]);
    setHistoryError("");
    setBudgetsEntries([]);
    setBudgetsError("");
    setBudgetsLoading(false);
    setExpandedBudgetIds([]);
    loadLeadHistory(lead.id);
    loadLeadDetail(lead.id);
    setEditOpen(true);
  };

  const handleDetailTabChange = (tab) => {
    if (detailActiveTab === tab) return;
    setDetailActiveTab(tab);
    if (tab === "history") {
      loadLeadHistory();
    } else if (tab === "budgets") {
      loadLeadBudgets();
    }
  };

  const DETAIL_TABS = [
    { id: "details", label: "Informações", icon: HiInformationCircle },
    { id: "history", label: "Histórico", icon: HiClock },
    { id: "budgets", label: "Orçamentos", icon: HiOutlineDocumentText },
    { id: "funnels", label: "Funis", icon: HiFunnel },
  ];

  const startStatusEdit = (lead) => {
    if (!lead?.id) return;
    setStatusEditingLeadId(lead.id);
    setStatusEditingValue(lead.status || "working");
  };

  const handleStatusSelect = async (leadId, value) => {
    setStatusEditingValue(value);
    setStatusUpdatingId(leadId);
    try {
      await axios.patch(LEADS.STATUS(leadId), { status: value });
      setStatusEditingLeadId(null);
      await refetchLeads();
    } catch (error) {
      console.error("Erro ao atualizar status do contato:", error);
      window.alert("Não foi possível atualizar o status do contato.");
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const renderStatusControl = (lead) => {
    const isEditing = statusEditingLeadId === lead.id;
    if (isEditing) {
      return (
        <Select
          data-inline-edit
          id={`status-select-${lead.id}`}
          value={statusEditingValue}
          onChange={(e) => handleStatusSelect(lead.id, e.target.value)}
          disabled={statusUpdatingId === lead.id}
          className="text-sm"
        >
          {LEAD_STATUS.map((statusOption) => (
            <option key={statusOption} value={statusOption}>
              {STATUS_META[statusOption]?.label || statusOption}
            </option>
          ))}
        </Select>
      );
    }

    return (
      <button
        type="button"
        className="inline-flex rounded-full border-0 bg-transparent p-0"
        onClick={() => startStatusEdit(lead)}
      >
        <StatusBadge status={lead.status} />
      </button>
    );
  };

  const startTypeEdit = (lead) => {
    if (!lead?.id) return;
    setTypeEditingLeadId(lead.id);
    setTypeEditingValue(lead.leadType || "lead");
  };

  const handleTypeSelect = async (leadId, value) => {
    setTypeEditingValue(value);
    setTypeUpdatingId(leadId);
    try {
      await axios.put(LEADS.UPDATE(leadId), { leadType: value });
      setTypeEditingLeadId(null);
      refetchLeads();
    } catch (error) {
      console.error("Erro ao atualizar tipo do lead:", error);
      window.alert("Não foi possível atualizar o tipo.");
    } finally {
      setTypeUpdatingId(null);
    }
  };

  const renderTypeControl = (lead) => {
    const isEditing = typeEditingLeadId === lead.id;
    if (isEditing) {
      return (
        <Select
          data-inline-edit
          id={`type-select-${lead.id}`}
          value={typeEditingValue}
          onChange={(e) => handleTypeSelect(lead.id, e.target.value)}
          disabled={typeUpdatingId === lead.id}
          className="text-sm"
        >
          {LEAD_TYPE.map((typeOption) => (
            <option key={typeOption} value={typeOption}>
              {LEAD_TYPE_META[typeOption]?.label || typeOption}
            </option>
          ))}
        </Select>
      );
    }

    return (
      <button
        type="button"
        className="inline-flex rounded-full border-0 bg-transparent p-0"
        data-inline-edit
        onClick={() => startTypeEdit(lead)}
      >
        <TypeBadge type={lead.leadType} />
      </button>
    );
  };

  const startSourceEdit = (lead) => {
    if (!lead?.id) return;
    setSourceEditingLeadId(lead.id);
    setSourceEditingValue(lead.source || "manual");
  };

  const handleSourceSelect = async (leadId, value) => {
    setSourceEditingValue(value);
    setSourceUpdatingId(leadId);
    try {
      await axios.put(LEADS.UPDATE(leadId), { source: value });
      setSourceEditingLeadId(null);
      await refetchLeads();
    } catch (error) {
      console.error("Erro ao atualizar origem do contato:", error);
      window.alert("Não foi possível atualizar a origem.");
    } finally {
      setSourceUpdatingId(null);
    }
  };

  const renderSourceControl = (lead) => {
    const isEditing = sourceEditingLeadId === lead.id;
    if (isEditing) {
      return (
        <Select
          data-inline-edit
          id={`source-select-${lead.id}`}
          value={sourceEditingValue}
          onChange={(e) => handleSourceSelect(lead.id, e.target.value)}
          disabled={sourceUpdatingId === lead.id}
          className="text-sm"
        >
          {LEAD_SOURCE.map((sourceOption) => (
            <option key={sourceOption} value={sourceOption}>
              {SOURCE_META[sourceOption]?.label || sourceOption}
            </option>
          ))}
        </Select>
      );
    }

    return (
      <button
        type="button"
        className="inline-flex rounded-full border-0 bg-transparent p-0"
        onClick={() => startSourceEdit(lead)}
      >
        <SourceBadge source={lead.source} />
      </button>
    );
  };

  const handleDeleteLead = async (lead) => {
    if (!lead?.id) return;
    const confirmed = window.confirm(
      `Excluir contato "${lead.name || lead.companyName || "?"}"?`
    );
    if (!confirmed) return;

    setDeletingLeadId(lead.id);
    try {
      await axios.delete(LEADS.DELETE(lead.id));
      await refetchLeads();
    } catch (error) {
      console.error("Erro ao remover contato:", error);
      window.alert("Não foi possível remover o contato.");
    } finally {
      setDeletingLeadId(null);
    }
  };

  // ===== Ações: abrir modais =====
  const onEdit = (lead) => {
    openDetailModal(lead);
  };

  const onSchedule = (lead) => {
    setLeadForSchedule(lead);
    // default: agora +1h
    const dt = new Date();
    dt.setHours(dt.getHours() + 1);
    const isoLocal = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    setSchedWhen(isoLocal);
    setSchedType("followup");
    setSchedNotes("");
  };

  const onConvert = (lead) => {
    setLeadForConvert(lead);
    setConvertOpen(true);
  };

  // ===== Persist: Schedule =====
  const saveSchedule = async () => {
    if (!leadForSchedule?.id || !schedWhen) return;
    try {
      setSavingSchedule(true);
      await axios.post(`${LEADS.LIST}/${leadForSchedule.id}/tasks`, {
        type: schedType, // followup | ligacao | email | whatsapp | reuniao | visita | nota
        dueDate: new Date(schedWhen).toISOString(),
        notes: schedNotes || null,
      });
      setLeadForSchedule(null);
      await refetchLeads();
    } catch (e) {
      console.error("Erro ao agendar:", e);
    } finally {
      setSavingSchedule(false);
    }
  };

  const filterFunnelsByAccess = useCallback(
    (list = []) => {
      if (!Array.isArray(list) || !list.length) return [];
      const privileged = ["ADMIN", "POWER_USER"].includes(userPermission);
      if (privileged) return list;
      const normalizedUserId = user?.id ? String(user.id) : null;
      const normalizedTeamIds = userTeamIds.map((id) => String(id));

      return list.filter((funnel) => {
        if (!funnel) return false;
        const visibility = String(funnel.visibility || "PRIVATE").toUpperCase();
        if (visibility === "PUBLIC") return true;
        if (visibility === "PRIVATE" && normalizedUserId) {
          return String(funnel.ownerUserId) === normalizedUserId;
        }
        if (visibility === "TEAM" && funnel.teamId) {
          return normalizedTeamIds.includes(String(funnel.teamId));
        }
        return false;
      });
    },
    [user, userPermission, userTeamIds]
  );

  const loadFunnels = useCallback(async () => {
    try {
      const res = await axios.get(CRM.FUNNELS);
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      const filtered = filterFunnelsByAccess(list);
      setFunnels(filtered);
      return filtered;
    } catch (e) {
      console.error("Erro ao listar funis:", e);
      return [];
    }
  }, [filterFunnelsByAccess]);

  const fetchFunnelDetails = useCallback(async (id) => {
    if (!id) return null;
    try {
      const res = await axios.get(`${CRM.FUNNEL_DETAILS}/${id}/details`);
      return res?.data?.data || null;
    } catch (e) {
      console.error("Erro ao listar etapas do funil:", e);
      return null;
    }
  }, []);

  const ensureResponsibleSelection = (opts) => {
    if (!opts.length) return "";
    return String(opts[0].id);
  };

  const loadResponsibleOptionsFor = useCallback(
    async (funnel, setterOptions, setterId) => {
      if (!funnel) {
        setterOptions([]);
        setterId("");
        return;
      }
      const visibility = String(funnel.visibility || "PUBLIC").toUpperCase();
      const fallback = mapUsersToOptions(ownersData);
      if (visibility === "PRIVATE") {
        const owner =
          funnel.owner ||
          fallback.find((o) => String(o.id) === String(funnel.ownerUserId));
        const opts = owner ? [owner] : fallback;
        setterOptions(opts);
        setterId(ensureResponsibleSelection(opts));
        return;
      }
      if (visibility === "TEAM" && funnel.teamId) {
        try {
          const res = await axios.get(TEAMS.LIST_USERS(funnel.teamId));
          const opts = mapUsersToOptions(
            res?.data?.data || res?.data || res?.users || []
          );
          if (opts.length) {
            setterOptions(opts);
            setterId(ensureResponsibleSelection(opts));
            return;
          }
        } catch (err) {
          console.error("Erro ao listar responsáveis do time:", err);
        }
      }
      setterOptions(fallback);
      setterId(ensureResponsibleSelection(fallback));
    },
    [ownersData]
  );

  useEffect(() => {
    if (!convertOpen) return;
    (async () => {
      const list = await loadFunnels();
      if (list[0]) {
        setFunnelId(list[0].id);
      }
    })();
  }, [convertOpen, loadFunnels]);

  useEffect(() => {
    if (!funnelId) {
      setStages([]);
      setSelectedFunnelDetail(null);
      return;
    }
    (async () => {
      const detail = await fetchFunnelDetails(funnelId);
      setSelectedFunnelDetail(detail);
      const st = detail?.stages || [];
      setStages(st);
      if (st[0]) {
        setStageId(st[0].id);
      }
    })();
  }, [funnelId, fetchFunnelDetails]);

  useEffect(() => {
    if (!convertOpen) return;
    const funnel =
      selectedFunnelDetail ||
      funnels.find((f) => String(f.id) === String(funnelId));
    loadResponsibleOptionsFor(funnel, setResponsibleOptions, setResponsibleId);
  }, [
    convertOpen,
    funnelId,
    selectedFunnelDetail,
    funnels,
    ownersData,
    loadResponsibleOptionsFor,
  ]);

  useEffect(() => {
    if (!bulkModalOpen) return;
    loadFunnels().then((list) => {
      if (!bulkFunnelId && list[0]) {
        setBulkFunnelId(list[0].id);
      }
    });
  }, [bulkModalOpen, bulkFunnelId, loadFunnels]);

  useEffect(() => {
    if (!bulkModalOpen || !bulkFunnelId) {
      setBulkStages([]);
      setBulkStageId("");
      return;
    }
    (async () => {
      const detail = await fetchFunnelDetails(bulkFunnelId);
      const st = detail?.stages || [];
      setBulkStages(st);
      setBulkStageId(st[0]?.id || "");
    })();
  }, [bulkModalOpen, bulkFunnelId, fetchFunnelDetails]);

  useEffect(() => {
    if (!bulkModalOpen) return;
    const funnel = funnels.find((f) => String(f.id) === String(bulkFunnelId));
    loadResponsibleOptionsFor(
      funnel,
      setBulkResponsibleOptions,
      setBulkResponsibleId
    );
  }, [
    bulkModalOpen,
    bulkFunnelId,
    funnels,
    ownersData,
    loadResponsibleOptionsFor,
  ]);

  useEffect(() => {
    if (!newActionsOpen) {
      setNewMenuSlideIn(false);
      return;
    }
    requestAnimationFrame(() => setNewMenuSlideIn(true));
  }, [newActionsOpen]);

  useEffect(() => {
    if (!mobileLeadMenuOpen) {
      setMobileMenuSlideIn(false);
      return;
    }
    requestAnimationFrame(() => setMobileMenuSlideIn(true));
  }, [mobileLeadMenuOpen]);

  const saveConvert = async () => {
    if (!leadForConvert?.id || !stageId) return;
    try {
      setSavingConvert(true);
      const cardRes = await axios.post(`${CRM.CREATE_CARD}/${stageId}`, {
        title: cardTitle || leadForConvert.name || "Novo card",
        leadId: leadForConvert.id,
        responsibleId: responsibleId || null,
        priority: 1,
      });

      const cardId = cardRes?.data?.data?.id || null;

      await axios.put(`${LEADS.LIST}/${leadForConvert.id}`, {
        status: "qualified",
        convertedAt: new Date().toISOString(),
        convertedCardId: cardId,
      });

      setConvertOpen(false);
      setLeadForConvert(null);
      await refetchLeads();
    } catch (e) {
      console.error("Erro ao converter contato:", e);
    } finally {
      setSavingConvert(false);
    }
  };

  const closeBulkModal = () => {
    setBulkModalOpen(false);
    setBulkStages([]);
    setBulkStageId("");
    setBulkResponsibleOptions([]);
    setBulkResponsibleId("");
  };

  const handleBulkImport = async () => {
    if (!bulkStageId || !selectedCount) return;
    setBulkSaving(true);
    try {
      for (const leadId of selectedLeadIds) {
        const lead = leadById.get(leadId);
        const title =
          lead?.name || lead?.companyName || "Novo card para contato";
        const cardRes = await axios.post(`${CRM.CREATE_CARD}/${bulkStageId}`, {
          title,
          leadId,
          responsibleId: bulkResponsibleId || null,
          priority: 1,
        });
        const cardId = cardRes?.data?.data?.id || null;
        await axios.put(`${LEADS.LIST}/${leadId}`, {
          status: "qualified",
          convertedAt: new Date().toISOString(),
          convertedCardId: cardId,
        });
      }
      closeBulkModal();
      clearSelection();
      await refetchLeads();
    } catch (e) {
      console.error("Erro ao importar contatos para o funil:", e);
    } finally {
      setBulkSaving(false);
    }
  };

  // ===== UI =====
  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-1">
        <p className="text-sm uppercase tracking-[0.4em] text-orange-500">
          Contatos
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Lista de contatos
            </h1>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="hidden md:flex items-center gap-2"
              onClick={() => setCreateOpen(true)}
            >
              Novo contato
            </Button>
            <Button
              className="text-sm whitespace-nowrap inline-flex items-center gap-2 bg-blue-700 text-white hidden md:inline-flex"
              onClick={() => setImportOpen(true)}
            >
              Importar contatos
            </Button>

            <Button
              className="!text-sm whitespace-nowrap inline-flex items-center gap-2 bg-gray-500 !text-white hidden md:flex"
              onClick={() => {
                navigate("/contacts/custom-fields");
              }}
            >
              <div className="flex items-center gap-2">
                <span className="hidden md:block">Campos customizados</span>
              </div>
            </Button>
          </div>
        </div>
      </header>
      {/* HEADER / CONTROLS */}
      <div className="rounded-2xl border border-gray-200 bg-gray-50 shadow-sm">
        <div className="p-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <IoMdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                  }}
                  placeholder="Buscar por nome, e-mail, telefone, empresa…"
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 focus:border-gray-300 focus:ring-0 text-sm"
                />
              </div>
              <Button
                className="!text-sm whitespace-nowrap inline-flex items-center gap-2 bg-gray-500 !text-white md:hidden"
                onClick={() => {
                  refetchOwners();
                  setFiltersOpen(true);
                }}
              >
                <HiOutlineAdjustmentsHorizontal className="text-base" />
              </Button>
            </div>
          </div>

          <div className="mt-3 hidden md:grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="min-w-[220px]">
              <label className="block text-[11px] text-gray-500 mb-1">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
                className="w-full text-sm rounded-lg border-gray-200"
              >
                <option value="all">Todos</option>
                {LEAD_STATUS.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_META[s]?.label || s}
                  </option>
                ))}
              </select>
            </div>

            <div className="min-w-[220px]">
              <label className="block text-[11px] text-gray-500 mb-1">
                Origem
              </label>
              <select
                value={source}
                onChange={(e) => {
                  setSource(e.target.value);
                  setPage(1);
                }}
                className="w-full text-sm rounded-lg border-gray-200"
              >
                <option value="all">Todas</option>
                {LEAD_SOURCE.map((s) => (
                  <option key={s} value={s}>
                    {SOURCE_META[s]?.label || s}
                  </option>
                ))}
              </select>
            </div>

            <div className="min-w-[220px]">
              <label className="block text-[11px] text-gray-500 mb-1">
                Tipo
              </label>
              <select
                value={leadType}
                onChange={(e) => {
                  setLeadType(e.target.value);
                  setPage(1);
                }}
                className="w-full text-sm rounded-lg border-gray-200"
              >
                <option value="all">Todos</option>
                {LEAD_TYPE.map((lt) => (
                  <option key={lt} value={lt}>
                    {LEAD_TYPE_META[lt]?.label || lt}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-2 items-center justify-end mt-3">
            <Button
              size="sm"
              className="hidden md:flex items-center gap-2 px-2"
              onClick={() => applyFilters()}
            >
              Filtrar
            </Button>

            <Button
              color="light"
              onClick={clearAll}
              className="text-sm hidden md:block"
            >
              Limpar
            </Button>
          </div>

          {activeChips.length > 0 && (
            <div className="mt-3 -mx-2">
              <div className="px-2 flex gap-1.5 overflow-x-auto no-scrollbar">
                {activeChips.map((ch) => (
                  <span
                    key={ch.k}
                    className="inline-flex items-center gap-1.5 bg-white text-gray-700 px-2 py-0.5 rounded-full text-[11px] border border-gray-200 shrink-0"
                  >
                    {ch.label}
                    <button
                      className="ml-0.5 text-gray-500 hover:text-gray-800"
                      onClick={ch.clear}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* floating actions button */}
      <div className="md:hidden fixed bottom-4 right-4 z-50">
        <Button
          className="h-14 w-14 rounded-full p-0 flex items-center justify-center bg-primary text-white shadow-xl"
          onClick={openNewActions}
          aria-label="Ações rápidas"
        >
          <HiPlus className="text-xl" />
        </Button>
      </div>

      {selectedCount > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-600">
          <span>{selectedCount} contato(s) selecionado(s)</span>
          <Button size="xs" color="light" onClick={clearSelection}>
            Limpar seleção
          </Button>
          <Button
            className="!text-sm whitespace-nowrap inline-flex items-center gap-2 !text-white"
            size="xs"
            onClick={() => setBulkModalOpen(true)}
            disabled={!selectedCount}
          >
            <span>Importar para funil ({selectedCount})</span>
          </Button>
        </div>
      )}

      {/* MOBILE CARDS */}
      <div className="grid grid-cols-1 gap-3 md:hidden">
        {!isLoading ? (
          rows.length ? (
            rows.map((lead) => (
              <div
                key={lead.id}
                className="rounded-xl border border-gray-200 bg-white shadow-sm p-3 cursor-pointer"
                onClick={() => openMobileLeadMenu(lead)}
              >
                {/* Cabeçalho */}
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div
                      className="text-gray-900 font-medium leading-tight truncate"
                      title={lead?.name || "—"}
                    >
                      {lead?.name || "—"}
                    </div>
                    <div
                      className="text-[12px] text-gray-500 truncate"
                      title={lead?.email || "—"}
                    >
                      {lead?.email || "Sem E-mail"}
                    </div>
                    <div className="text-[12px] text-gray-500 truncate">
                      {formatPhoneNumber(lead?.phoneNumber) || "—"}
                    </div>
                    {lead?.tag && (
                      <div className="mt-1 flex items-center gap-1 text-[10px] min-w-0">
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ background: lead.tag.color || "#999" }}
                        />
                        <span className="truncate" title={lead.tag.name}>
                          {lead.tag.name}
                        </span>
                      </div>
                    )}
                    <div className="mt-1">
                      <StageInfoCell stage={lead.stage} cards={lead.cards} />
                    </div>
                    <div className="mt-1 flex justify-start">
                      {renderStatusControl(lead)}
                    </div>
                  </div>

                  <div className="text-right space-y-1">
                    <div className="flex justify-end">
                      {renderTypeControl(lead)}
                    </div>
                    <div className="flex justify-end">
                      {renderSourceControl(lead)}
                    </div>
                  </div>
                </div>

                {/* Próxima ação + Dono */}
                <div className="mt-2 flex items-center justify-end">
                  <div className="text-[11px] text-gray-500">
                    Dono:{" "}
                    <span className="font-medium text-gray-800">
                      {lead?.owner?.name || "—"}
                    </span>
                  </div>
                </div>

                {/* Ações */}
              </div>
            ))
          ) : (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-gray-500">
              Nenhum contato encontrado.
            </div>
          )
        ) : (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-xl border border-gray-200 p-3"
              >
                <div className="h-4 bg-gray-100 rounded w-2/3 mb-2" />
                <div className="h-3 bg-gray-100 rounded w-1/2 mb-1" />
                <div className="h-3 bg-gray-100 rounded w-1/3" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* DESKTOP TABLE */}
      <div className="hidden md:block">
        <div className="overflow-x-auto">
          <div className="min-w-full rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <Table striped={false} className="text-sm">
              <Table.Head className="uppercase text-[11px] tracking-wide sticky top-0 z-5">
                <Table.HeadCell className="bg-gray-100 w-[48px] text-center">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    aria-label="Selecionar todos"
                  />
                </Table.HeadCell>
                <Table.HeadCell className="bg-gray-100">Contato</Table.HeadCell>
                <Table.HeadCell className="bg-gray-100 text-left">
                  Telefone/E-mail
                </Table.HeadCell>
                <Table.HeadCell className="bg-gray-100 text-center">
                  Origem
                </Table.HeadCell>
                <Table.HeadCell className="bg-gray-100 text-center">
                  Tipo
                </Table.HeadCell>
                <Table.HeadCell className="bg-gray-100 text-left">
                  Funil · Etapa
                </Table.HeadCell>
                <Table.HeadCell className="bg-gray-100 text-center">
                  Ações
                </Table.HeadCell>
              </Table.Head>

              <Table.Body className="divide-y divide-gray-100">
                {!isLoading ? (
                  rows.length ? (
                    rows.map((lead, idx) => (
                      <Table.Row
                        key={lead.id}
                        className={idx % 2 ? "bg-white" : "bg-gray-50/40"}
                      >
                        <Table.Cell className="py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedLeadIds.has(lead.id)}
                            onChange={() => toggleLeadSelection(lead.id)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </Table.Cell>
                        <Table.Cell className="py-3 align-middle">
                          {/* min-w-0 é importante quando o pai usa flex/grid; max-w força o corte */}
                          <div className="min-w-0 max-w-[260px] sm:max-w-[320px]">
                            {/* Nome */}
                            <div
                              className="font-medium text-gray-900 truncate"
                              title={lead?.name || "—"}
                            >
                              {lead?.name || "—"}
                            </div>

                            {/* Tag */}
                            {lead?.tag && (
                              <div className="mt-1 flex items-center gap-1 text-[10px] min-w-0">
                                <span
                                  className="h-2 w-2 shrink-0 rounded-full"
                                  style={{
                                    background: lead.tag.color || "#999",
                                  }}
                                />
                                <span
                                  className="truncate"
                                  title={lead.tag.name}
                                >
                                  {lead.tag.name}
                                </span>
                              </div>
                            )}
                            <div className="mt-0 flex items-center gap-1">
                              <span className="text-[11px]">Status:</span>{" "}
                              {renderStatusControl(lead)}
                            </div>
                          </div>
                        </Table.Cell>

                        <Table.Cell className="py-3 text-left">
                          <div className="text-gray-900">
                            {formatPhoneNumber(lead.phoneNumber) || "—"}
                          </div>
                          <div className="text-xs text-gray-500">
                            {lead.email || "Nenhum e-mail informado"}
                          </div>
                        </Table.Cell>

                        <Table.Cell className="py-3 text-center">
                          {renderSourceControl(lead)}
                        </Table.Cell>

                        <Table.Cell className="py-3 text-center">
                          {renderTypeControl(lead)}
                        </Table.Cell>

                        <Table.Cell className="py-3 text-center">
                          <StageInfoCell
                            stage={lead.stage}
                            cards={lead.cards}
                          />
                        </Table.Cell>

                        <Table.Cell className="py-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              size="xs"
                              color="success"
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openCardModal(lead);
                              }}
                              title="Criar card no funil"
                              aria-label="Criar card no funil"
                            >
                              <HiFunnel className="text-base" />
                            </Button>

                            <Button
                              size="xs"
                              color="light"
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit(lead);
                              }}
                              title="Ver detalhes"
                              aria-label="Ver detalhes"
                            >
                              <HiOutlineDocumentText className="text-base" />
                            </Button>
                            <Button
                              size="xs"
                              color="failure"
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteLead(lead);
                              }}
                              title="Excluir"
                              aria-label="Excluir lead"
                              disabled={deletingLeadId === lead.id}
                            >
                              <HiOutlineTrash className="text-base" />
                            </Button>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    ))
                  ) : (
                    <Table.Row>
                      <Table.Cell
                        colSpan={7}
                        className="text-center py-10 text-gray-500"
                      >
                        Nenhum lead encontrado.
                      </Table.Cell>
                    </Table.Row>
                  )
                ) : (
                  <Table.Row>
                    <Table.Cell colSpan={7} className="py-6">
                      <div className="animate-pulse space-y-3">
                        <div className="h-4 bg-gray-100 rounded w-2/3" />
                        <div className="h-3 bg-gray-100 rounded w-1/2" />
                        <div className="h-3 bg-gray-100 rounded w-1/3" />
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </div>
        </div>
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex overflow-x-auto justify-center">
          <Pagination
            layout="pagination"
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            previousLabel=""
            nextLabel=""
            showIcons
          />
        </div>
      )}

      {newActionsOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeNewActions}
          />
          <div
            className={`relative w-full rounded-t-xl bg-white px-6 pt-3 pb-8 shadow-[0_0_24px_rgba(15,23,42,0.25)] transition-transform duration-300 ease-out transform ${
              newMenuSlideIn ? "translate-y-0" : "translate-y-full"
            }`}
          >
            <div className="mx-auto mb-3 h-1.5 w-16 rounded-full bg-slate-200"></div>
            <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
              Ações rápidas
            </p>
            <div className="space-y-3 mt-3">
              <Button
                fullWidth
                className="rounded-2xl py-3 text-base font-semibold w-full"
                onClick={handleNewContact}
              >
                Cadastrar novo lead
              </Button>
              <Button
                fullWidth
                className="rounded-2xl py-3 text-base font-semibold w-full bg-blue-500"
                onClick={handleImportContacts}
              >
                Importar contatos
              </Button>
              <Button
                fullWidth
                className="rounded-2xl py-3 text-base font-semibold w-full bg-gray-500"
                onClick={handleImportContacts}
              >
                Campos Customizados
              </Button>
            </div>
          </div>
        </div>
      )}

      {mobileLeadMenuOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeMobileLeadMenu}
          />
          <div
            className={`relative w-full rounded-t-xl bg-white px-6 pt-3 pb-8 shadow-[0_0_24px_rgba(15,23,42,0.25)] transition-transform duration-300 ease-out transform ${
              mobileMenuSlideIn ? "translate-y-0" : "translate-y-full"
            }`}
          >
            <div className="mx-auto mb-3 h-1.5 w-16 rounded-full bg-slate-200"></div>
            <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
              Ações rápidas
            </p>
            <div className="space-y-3 mt-3">
              <Button
                color="success"
                fullWidth
                className="rounded-2xl py-3 text-base font-semibold w-full"
                onClick={handleMobileLeadConvert}
              >
                Criar card no funil
              </Button>
              <Button
                color="light"
                fullWidth
                className="rounded-2xl py-3 text-base font-semibold w-full"
                onClick={handleMobileLeadEdit}
              >
                Ver detalhes
              </Button>
              <Button
                color="failure"
                fullWidth
                className="rounded-2xl py-3 text-base font-semibold w-full"
                onClick={handleMobileLeadDelete}
              >
                Excluir contato
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: AGENDAR ===== */}
      <Modal
        show={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        size="2xl"
      >
        <Modal.Header>Mais filtros</Modal.Header>
        <Modal.Body>
          <div className="space-y-3 md:hidden">
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
                className="w-full text-sm rounded-lg border-gray-200"
              >
                <option value="all">Todos</option>
                {LEAD_STATUS.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_META[s]?.label || s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">
                Origem
              </label>
              <select
                value={source}
                onChange={(e) => {
                  setSource(e.target.value);
                  setPage(1);
                }}
                className="w-full text-sm rounded-lg border-gray-200"
              >
                <option value="all">Todas</option>
                {LEAD_SOURCE.map((s) => (
                  <option key={s} value={s}>
                    {SOURCE_META[s]?.label || s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">
                Tipo
              </label>
              <select
                value={leadType}
                onChange={(e) => {
                  setLeadType(e.target.value);
                  setPage(1);
                }}
                className="w-full text-sm rounded-lg border-gray-200"
              >
                <option value="all">Todos</option>
                {LEAD_TYPE.map((lt) => (
                  <option key={lt} value={lt}>
                    {LEAD_TYPE_META[lt]?.label || lt}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">
                Dono
              </label>
              <select
                value={ownerId}
                onChange={(e) => {
                  setOwnerId(e.target.value);
                  setPage(1);
                }}
                className="w-full text-sm rounded-lg border-gray-200"
                disabled={isUserOnly}
              >
                {isUserOnly ? (
                  <option value={user?.id || "all"}>
                    {user?.name ? `Somente ${user.name}` : "Somente meus leads"}
                  </option>
                ) : (
                  <>
                    <option value="all">Todos</option>
                    {ownersLoading ? (
                      <option value="" disabled>
                        Carregando usuários…
                      </option>
                    ) : ownersData.length ? (
                      ownersData.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        Nenhum usuário encontrado
                      </option>
                    )}
                  </>
                )}
              </select>
            </div>

            <div>
              <label className="block text-[11px] text-gray-500 mb-1">
                Tag
              </label>
              <select
                value={tagId}
                onChange={(e) => {
                  setTagId(e.target.value);
                  setPage(1);
                }}
                className="w-full text-sm rounded-lg border-gray-200"
              >
                <option value="all">Todas</option>
                {tags.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] text-gray-500 mb-1">
                Itens por página
              </label>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="w-full text-sm rounded-lg border-gray-200"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}/página
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="w-full"
            onClick={() => {
              applyFilters();
              setFiltersOpen(false);
            }}
          >
            Aplicar filtros
          </Button>
          <Button color="gray" onClick={() => setFiltersOpen(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!leadForSchedule} onClose={() => setLeadForSchedule(null)}>
        <Modal.Header>Agendar próxima ação</Modal.Header>
        <Modal.Body>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Tipo</label>
              <select
                value={schedType}
                onChange={(e) => setSchedType(e.target.value)}
                className="w-full rounded-lg border-gray-200"
              >
                <option value="followup">Follow-up</option>
                <option value="ligacao">Ligação</option>
                <option value="email">E-mail</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="reuniao">Reunião</option>
                <option value="visita">Visita</option>
                <option value="nota">Nota</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Quando</label>
              <input
                type="datetime-local"
                value={schedWhen}
                onChange={(e) => setSchedWhen(e.target.value)}
                className="w-full rounded-lg border-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Notas</label>
              <textarea
                rows={3}
                value={schedNotes}
                onChange={(e) => setSchedNotes(e.target.value)}
                className="w-full rounded-lg border-gray-200"
                placeholder="Contexto / objetivo da ação…"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => setLeadForSchedule(null)}>
            Cancelar
          </Button>
          <Button
            onClick={saveSchedule}
            isProcessing={savingSchedule}
            disabled={!schedWhen || savingSchedule}
          >
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={bulkModalOpen} onClose={closeBulkModal} size="lg">
        <Modal.Header>Importar contatos para o funil</Modal.Header>
        <Modal.Body>
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              {selectedCount} contato(s) selecionado(s).
            </p>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Funil
                </label>
                <select
                  className="w-full rounded-lg border-gray-200"
                  value={bulkFunnelId}
                  onChange={(e) => setBulkFunnelId(e.target.value)}
                >
                  {funnels.length ? (
                    funnels.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      Nenhum funil disponível
                    </option>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Etapa
                </label>
                <select
                  className="w-full rounded-lg border-gray-200"
                  value={bulkStageId}
                  onChange={(e) => setBulkStageId(e.target.value)}
                  disabled={!bulkStages.length}
                >
                  {bulkStages.map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Responsável (opcional)
                </label>
                <select
                  className="w-full rounded-lg border-gray-200"
                  value={bulkResponsibleId}
                  onChange={(e) => setBulkResponsibleId(e.target.value)}
                >
                  <option value="">—</option>
                  {bulkResponsibleOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-center gap-2 w-full">
            <Button
              onClick={handleBulkImport}
              isProcessing={bulkSaving}
              disabled={bulkSaving || !bulkStageId || !selectedCount}
            >
              Importar
            </Button>
            <Button color="gray" onClick={closeBulkModal}>
              Cancelar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ===== MODAL: CRIAR CARD NO FUNIL ===== */}
      <Modal
        show={convertOpen}
        onClose={() => {
          setConvertOpen(false);
          setLeadForConvert(null);
        }}
        size="lg"
      >
        <Modal.Header>Criar card para o funil</Modal.Header>
        <Modal.Body>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Lead</label>
              <input
                className="w-full rounded-lg border-gray-200 bg-white"
                value={
                  leadForConvert?.name || leadForConvert?.companyName || ""
                }
                disabled
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Funil
                </label>
                <select
                  className="w-full rounded-lg border-gray-200"
                  value={funnelId}
                  onChange={(e) => setFunnelId(e.target.value)}
                >
                  {funnels.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Etapa
                </label>
                <select
                  className="w-full rounded-lg border-gray-200"
                  value={stageId}
                  onChange={(e) => setStageId(e.target.value)}
                  disabled={!stages.length}
                >
                  {stages.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-1">
                Título do card
              </label>
              <input
                className="w-full rounded-lg border-gray-200"
                value={cardTitle}
                onChange={(e) => setCardTitle(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-1">
                Responsável (opcional)
              </label>
              <select
                className="w-full rounded-lg border-gray-200"
                value={responsibleId}
                onChange={(e) => setResponsibleId(e.target.value)}
              >
                <option value="">—</option>
                {responsibleOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-center gap-2 w-full">
            <Button
              onClick={saveConvert}
              isProcessing={savingConvert}
              disabled={savingConvert || !stageId}
            >
              Criar card
            </Button>
            <Button
              className="py-0.5"
              color="gray"
              onClick={() => setConvertOpen(false)}
            >
              Cancelar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ===== MODAL: DETALHES ===== */}
      <Modal show={editOpen} onClose={closeEditModal} size="4xl">
        <Modal.Header>
          <div className="text-sm mt-1">
            Detalhes ·
            <span className="ml-1 font-semibold">
              {leadForEdit?.name || leadForEdit?.companyName || "Lead"}
            </span>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-2 border-b border-slate-200 pb-3 mb-4">
            <div className="hidden md:flex flex-wrap gap-2">
              {DETAIL_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleDetailTabChange(tab.id)}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                    detailActiveTab === tab.id
                      ? "bg-slate-900 text-white shadow"
                      : "bg-white text-slate-600 border border-slate-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="md:hidden overflow-x-auto">
              <div className="flex gap-2 min-w-[280px]">
                {DETAIL_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleDetailTabChange(tab.id)}
                    className={`flex h-9 px-4 items-center justify-center rounded-xl border transition ${
                      detailActiveTab === tab.id
                        ? "bg-orange-500 text-white shadow"
                        : "bg-white text-slate-600 border border-slate-200"
                    }`}
                  >
                    <tab.icon className="h-5 w-5" />
                    <span className="sr-only">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          {leadDetailError && (
            <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {leadDetailError}
            </div>
          )}
          {detailActiveTab === "details" ? (
            editInitialData ? (
              <LeadCreateForm
                initialData={editInitialData}
                onSubmit={handleUpdateLead}
                submitLabel="Salvar alterações"
                formId="lead-edit-form"
                hideSubmitButton
                onSubmittingChange={setDetailSubmitting}
                onSuccess={handleEditSuccess}
              />
            ) : (
              <div className="text-sm text-gray-500">Carregando contato...</div>
            )
          ) : detailActiveTab === "history" ? (
            <div className="space-y-4">
              {historyLoading && (
                <div className="text-sm text-gray-500">
                  Carregando histórico...
                </div>
              )}
              {!historyLoading && historyError && (
                <div className="text-sm text-rose-600">{historyError}</div>
              )}
              {!historyLoading && !historyEntries.length && !historyError && (
                <div className="text-sm text-gray-500">
                  Nenhum evento histórico encontrado para este lead.
                </div>
              )}
              <div
                className="relative pl-10 overflow-y-auto"
                style={{ maxHeight: "350px" }}
              >
                <div className="absolute left-4 top-0 bottom-0 w-px bg-gradient-to-b from-orange-400 to-indigo-500" />
                <div className="space-y-3 mt-6">
                  {historyEntries.map((entry, index) => {
                    const detail = describeHistoryMetadata(entry.metadata);
                    return (
                      <article
                        key={entry.id || index}
                        className="relative gap-4"
                      >
                        <div
                          className="flex flex-col items-center text-orange-500 absolute mt-2"
                          style={{ left: "-30px" }}
                        >
                          <span className="h-3 w-3 rounded-full bg-orange-500 shadow-sm" />
                          <span className="mt-1 h-10 border-l border-dashed border-orange-300" />
                        </div>
                        <div className="flex-1 rounded-2xl border border-slate-200 bg-gray-50 to-slate-100 p-3 shadow-sm">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-base font-semibold text-slate-900">
                                {HISTORY_EVENT_LABELS[entry.eventType] ||
                                  entry.eventType ||
                                  "Evento"}
                              </p>
                            </div>
                            {entry.actor?.name && (
                              <span className="text-[12px] text-slate-500">
                                {entry.actor.name} -{" "}
                                {moment(entry.createdAt).format(
                                  "DD/MM/YYYY HH:mm"
                                )}
                              </span>
                            )}
                          </div>
                          <div className="mt-3 text-sm text-slate-600 space-y-2">
                            {entry.stage?.name && (
                              <p className="text-sm text-slate-700">
                                <span className="font-semibold text-slate-800">
                                  Etapa:
                                </span>{" "}
                                {entry.stage.name}
                              </p>
                            )}
                            {detail && (
                              <p className="text-[13px] text-slate-500">
                                {detail}
                              </p>
                            )}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : detailActiveTab === "funnels" ? (
            <div className="space-y-4">
              {!leadForEdit && (
                <div className="text-sm text-gray-500">
                  Carregando contato...
                </div>
              )}
              {leadForEdit && !funnelsEntries.length && (
                <div className="text-sm text-gray-500">
                  Nenhum funil associado a este contato.
                </div>
              )}
              {!!funnelsEntries.length && (
                <div className="space-y-3">
                  {funnelsEntries.map((entry) => (
                    <article
                      key={`${entry.funnelId}-${entry.cardId || entry.stageId}`}
                      className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                    >
                      <header className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                            Funil
                          </p>
                          <p className="text-base font-semibold text-slate-900">
                            {entry.funnelName}
                          </p>
                        </div>
                        {entry.updatedAt && (
                          <div className="text-xs text-slate-500">
                            Atualizado em{" "}
                            {moment(entry.updatedAt).format("DD/MM/YYYY HH:mm")}
                          </div>
                        )}
                      </header>
                      <div className="mt-3 grid grid-cols-2 gap-3 text-sm text-slate-600">
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                            Etapa atual
                          </p>
                          <p className="text-sm text-slate-900">
                            {entry.stageName}
                          </p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {budgetsLoading && (
                <div className="text-sm text-gray-500">
                  Carregando orçamentos...
                </div>
              )}
              {!budgetsLoading && budgetsError && (
                <div className="text-sm text-rose-600">{budgetsError}</div>
              )}
              {!budgetsLoading && !budgetsEntries.length && !budgetsError && (
                <div className="text-sm text-gray-500">
                  Nenhum orçamento encontrado para este lead.
                </div>
              )}
              {!!budgetsEntries.length && (
                <div className="space-y-3">
                  {budgetsEntries.map((budget) => {
                    const createdAt = budget.createdAt
                      ? moment(budget.createdAt).format("DD/MM/YYYY")
                      : "—";
                    const validUntil = budget.validUntil
                      ? moment(budget.validUntil).format("DD/MM/YYYY")
                      : "—";
                    const totalValue =
                      Number(budget.totalValue ?? budget.total ?? 0) || 0;
                    const statusColor =
                      BUDGET_STATUS_COLORS[
                        budget.status?.toLowerCase?.() ?? ""
                      ] || "gray";
                    const isExpanded = expandedBudgetIds.includes(budget.id);
                    const itemsList = Array.isArray(budget.items)
                      ? budget.items
                      : [];
                    return (
                      <article
                        key={budget.id}
                        className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                      >
                        <header className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">
                              Referência
                            </p>
                            <p className="text-base font-semibold text-slate-900">
                              {budget.reference || "—"}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-xs text-slate-500">
                              {createdAt}
                            </div>
                            <Button
                              size="xs"
                              color="light"
                              className="text-[11px]"
                              onClick={() => toggleBudgetItems(budget.id)}
                            >
                              {isExpanded ? "Ocultar itens" : "Ver itens"}
                            </Button>
                          </div>
                        </header>
                        <div className="mt-2 grid grid-cols-2 gap-3 text-sm text-slate-600">
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                              Validade
                            </p>
                            <p className="text-sm text-slate-900">
                              {validUntil}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                              Total
                            </p>
                            <p className="text-sm font-semibold text-slate-900">
                              {formatCurrency(totalValue)}
                            </p>
                          </div>
                        </div>
                        {isExpanded && itemsList.length > 0 && (
                          <div className="mt-3 border-t border-slate-200 pt-3">
                            {itemsList.map((item) => {
                              const itemTotal =
                                Number(item.totalValue) ||
                                (Number(item.unitPrice) || 0) *
                                  (Number(item.quantity) || 0);
                              return (
                                <div
                                  key={item.id || `${item.name}-${itemTotal}`}
                                  className="grid gap-3 text-xs text-slate-600 md:grid-cols-3"
                                >
                                  <div>
                                    <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                                      Item
                                    </p>
                                    <p className="text-sm text-slate-900">
                                      {item.name || item.description || "Item"}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                                      Quantidade
                                    </p>
                                    <p className="text-sm text-slate-900">
                                      {Number(item.quantity) || 0}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                                      Total
                                    </p>
                                    <p className="text-sm font-semibold text-slate-900">
                                      {formatCurrency(itemTotal)}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            form="lead-edit-form"
            type="submit"
            className="bg-primary"
            isProcessing={detailSubmitting}
            disabled={detailSubmitting}
          >
            Salvar alterações
          </Button>
          <Button color="gray" onClick={closeEditModal}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={createOpen} onClose={() => setCreateOpen(false)} size="4xl">
        <Modal.Header>Novo Contato</Modal.Header>
        <Modal.Body>
          <LeadCreateForm
            // se quiser definir um dono padrão:
            // defaultOwnerId={user?.id}
            onCreated={() => {
              setCreateOpen(false);
              refetchLeads(); // já existe acima
            }}
          />
        </Modal.Body>
      </Modal>

      <Modal show={importOpen} onClose={() => setImportOpen(false)} size="4xl">
        <Modal.Header>Importar contatos</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Carregue um arquivo Excel (XLSX) ou CSV estruturado com os campos
              abaixo. As colunas devem seguir exatamente o modelo mostrado para
              facilitar o mapeamento. Clique em “Baixar modelo” para obter um
              exemplo pronto.
            </p>
            <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-3">
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                Exemplo de modelo
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full table-fixed border border-gray-300 text-sm shadow-inner">
                  <thead className="bg-gray-50">
                    <tr>
                      {IMPORT_TEMPLATE.map((col) => (
                        <th
                          key={col.key}
                          className="border-b border-gray-300 px-3 py-2 text-left text-[11px] uppercase tracking-wide text-gray-500 whitespace-nowrap"
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white">
                      {IMPORT_SAMPLE.map((value, index) => (
                        <td
                          key={`${value}-${index}`}
                          className="border-b border-gray-200 px-3 py-2 text-[13px] text-gray-700"
                        >
                          {value}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-5 flex justify-end">
                <Button
                  size="xs"
                  onClick={downloadTemplate}
                  className="!text-sm bg-gray-500 text-white py-2"
                >
                  <div className="flex items-center gap-2">
                    <HiDownload />
                    <span>Baixar modelo de planilha</span>
                  </div>
                </Button>
              </div>
            </div>

            <div className="mt-10">
              <label className="block text-sm font-medium text-gray-600 mb-3">
                Faça o upload do seu arquivo Excel
              </label>
              <label className="relative flex items-center gap-3 rounded-2xl border-2 border-dashed border-orange-300 bg-orange-50/50 p-4 text-left text-sm font-medium text-orange-700 transition hover:border-orange-400 hover:bg-orange-50">
                <HiOutlineCloudArrowUp className="text-2xl" />
                <div>
                  <div>Arraste e solte o arquivo ou clique para selecionar</div>
                  <div className="text-xs text-orange-600">
                    Aceitamos .xlsx, .xls e .csv
                  </div>
                </div>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </label>
              {importFile && (
                <div className="mt-2 text-xs text-gray-500">
                  Arquivo pronto: {importFile.name}
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-2 w-full">
          <Button color="gray" onClick={() => setImportOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleImportSubmit}
            disabled={importing || !importFile}
          >
            {importing ? "Importando..." : "Importar contatos"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
